/**
 * Programmatic beep generator for the PDA scanning UX. Uses the Web Audio API
 * so we don't have to ship any MP3 / OGG assets with the build (and so they
 * sound the same on every WebView regardless of media-codec config).
 *
 * Four sounds in the family:
 *   - tick()     → short blip every time a NEW unique tag is added (kept very
 *                  short so a burst of 30 tags doesn't drown out the room)
 *   - ok()       → pleasant 2-note "ding" — successful bag barcode, etc.
 *   - error()    → low buzz + device vibration — wrong barcode / extra item
 *   - complete() → 3-note ascending chord — order delivery complete
 *
 * AudioContext is created lazily and reused. WebViews on Android require a
 * user gesture before audio can play; the first beep after the operator
 * taps something will resume the context if it's been suspended.
 */

let _ctx = null;
function ctx() {
  if (_ctx) return _ctx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    _ctx = new C();
  } catch {
    return null;
  }
  return _ctx;
}

function tone(freq, durMs, { type = "sine", gain = 0.18, startMs = 0 } = {}) {
  const ac = ctx();
  if (!ac) return;
  if (ac.state === "suspended") { try { ac.resume(); } catch {} }
  const t0 = ac.currentTime + startMs / 1000;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  // Quick attack + decay envelope — keeps clicks/pops out.
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + durMs / 1000);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + durMs / 1000 + 0.02);
}

function native(kind) {
  try {
    if (typeof window !== "undefined" && window.DrrtPda && typeof window.DrrtPda.playBeep === "function") {
      window.DrrtPda.playBeep(kind);
      return true;
    }
  } catch { /* bridge unavailable */ }
  return false;
}

export const beep = {
  tick: () => {
    if (native("tick")) return;
    tone(1200, 35, { gain: 0.12 });
  },
  ok: () => {
    if (native("ok")) return;
    tone(880, 80); tone(1320, 110, { startMs: 80 });
  },
  error: () => {
    if (native("error")) return;
    tone(220, 140, { type: "square", gain: 0.22 });
    tone(180, 180, { type: "square", gain: 0.22, startMs: 140 });
  },
  complete: () => {
    if (native("complete")) return;
    tone(660, 120);
    tone(880, 120, { startMs: 120 });
    tone(1320, 200, { startMs: 240 });
  },
};

/** Resume the AudioContext on the next user gesture if it's stuck suspended.
 *  Call once from a PDA page that mounts after login. */
export function primeAudio() {
  const ac = ctx();
  if (!ac) return;
  const wake = () => {
    try { ac.resume(); } catch {}
    window.removeEventListener("touchstart", wake);
    window.removeEventListener("click", wake);
  };
  if (ac.state === "suspended") {
    window.addEventListener("touchstart", wake, { once: true });
    window.addEventListener("click", wake, { once: true });
  }
}
